import "./style.css";
import { advanceSeason, createClock, seasonOf } from "./clock.ts";
import { createLedger, applySeasonTick } from "./economy.ts";
import { DEFAULT_MAP_RADIUS, generateHexMap } from "./map.ts";
import {
  acceptOath,
  alreadyCalledBannersThisTurn,
  answerLiegeCall,
  applyHostUpkeep,
  applyMuster,
  callBannermen,
  conscriptToGarrison,
  createCallState,
  dischargeFromGarrison,
  ensureCallState,
  freeAbleSeat,
  markAllHornsRead,
  markHornRead,
  postLiegeCall,
  shouldPostLiegeCall,
  releaseHostToBanners,
  seatArmsTotals,
  tickCallOnEndTurn,
  type CallOutcome,
} from "./call.ts";
import {
  demoteTroops,
  garrisonTotal,
  hostTotal,
  promoteTroops,
  type PoolKind,
  type TrainEdgeId,
} from "./troops.ts";
import {
  applyGrowthTick,
  canSpawnDaughter,
  createGrowthState,
  encourageGrowth,
  forceUnclaimRealmToCapacity,
  hungerTag,
  settlementPop,
  resolvePromoteSeat,
  resolveSpawnVillage,
  toggleSeatClaim,
  type GrowthState,
} from "./growth.ts";
import { renderApp, type RenderState } from "./render.ts";

const DEFAULT_SEED = "call-to-arms-phase3";
/** Play disk: r=14 packs seat + bannermen at ≥6-hex separation (was r=10). */
const RADIUS = DEFAULT_MAP_RADIUS;
/** Smaller hex so the larger disk still fits the stage. */
const HEX_SIZE = 14;

function freshSeed(): string {
  return `cta-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
}

function ledgerFromMap(map: ReturnType<typeof generateHexMap>) {
  const seat = map.cells.get(map.seatKey);
  const banners = map.bannermanKeys.map((key) => {
    const c = map.cells.get(key);
    return { key, name: c?.name ?? "Bannerman" };
  });
  return createLedger(banners, seat?.name ?? "Your Seat");
}

function buildState(seed: string, clock = createClock()): RenderState {
  const map = generateHexMap(seed, RADIUS);
  const banners = map.bannermanKeys.map((key) => {
    const c = map.cells.get(key);
    return { key, name: c?.name ?? "Bannerman" };
  });
  const call = ensureCallState(createCallState(undefined, banners));
  const ledger = ledgerFromMap(map);
  const growth = createGrowthState(map);
  // Seat chrome starts with default liege/garrison hints from call defaults
  const seat = {
    ...ledger.seat,
    liegeHint: `${call.liege.name}, ${call.liege.title} · favor ${call.liege.favor} (swear the oath)`,
    garrisonHint: `Garrison ${garrisonTotal(call.arms)} · host ${hostTotal(call.arms)}`,
  };
  return {
    map,
    clock,
    ledger: { ...ledger, seat },
    call,
    growth,
    selectedKey: null,
    hexSize: HEX_SIZE,
    toast: null,
  };
}

let state = buildState(DEFAULT_SEED);

function mount(): void {
  const root = document.querySelector<HTMLElement>("#app");
  if (!root) throw new Error("#app missing");

  const paint = () => {
    renderApp(root, state, {
      onEndTurn: () => {
        // In-transit banner spears arrive; pending seat levy/dismiss finalizes;
        // liege delay clock ticks / soft-refuse
        const ticked = tickCallOnEndTurn(state.call, state.growth, {
          turn: state.clock.turn,
          map: state.map,
          season: seasonOf(state.clock),
        });
        let call = ticked.call;
        let ledger = state.ledger;
        let growth: GrowthState = ticked.growth ?? state.growth;
        if (ticked.arrived > 0 || ticked.levyResolved > 0 || ticked.log.length > 0) {
          ledger = {
            ...ledger,
            seat: {
              ...ledger.seat,
              garrisonHint:
                `Garrison ${garrisonTotal(call.arms)} · host ${hostTotal(call.arms)}` +
                (ticked.arrived > 0 ? ` (+${ticked.arrived} arrived)` : "") +
                (ticked.levyResolved > 0 ? ` · levy ±${ticked.levyResolved}` : ""),
            },
            lastTickLog: [...ticked.log, ...ledger.lastTickLog].slice(0, 16),
          };
        }

        const season = seasonOf(state.clock);
        // Binding: settlementPop drop (conscript) can leave claims over capacity —
        // auto-trim farthest excess before food/growth math sees the map.
        {
          const beforeLog = growth.lastLog[0] ?? null;
          growth = forceUnclaimRealmToCapacity(growth);
          if (growth.lastLog[0] && growth.lastLog[0] !== beforeLog) {
            ledger = {
              ...ledger,
              lastTickLog: [growth.lastLog[0], ...ledger.lastTickLog].slice(0, 16),
            };
          }
        }
        // 3.10: claim food + mouths = settlement + garr/host (+ banner garr/transit)
        {
          const mil = seatArmsTotals(call);
          ledger = applySeasonTick(ledger, season, state.map, growth, {
            arms: {
              garrison: mil.playerGarrison,
              host: mil.playerHost,
              bannersGarrisoned: mil.bannersGarrisoned,
              bannersInTransit: mil.bannersInTransit,
            },
          });
          // Growth tick after stores update (uses food cushion)
          const bannerMilitary: Record<string, number> = {};
          for (const s of call.bannerSeats) {
            bannerMilitary[s.key] = s.garrisoned + s.inTransit;
          }
          growth = applyGrowthTick(growth, season, ledger.stocks.food, {
            seatMilitary: mil.playerGarrison + mil.playerHost,
            bannerMilitary,
          });
        }
        if (growth.lastLog.length > 0) {
          ledger = {
            ...ledger,
            lastTickLog: [...growth.lastLog, ...ledger.lastTickLog].slice(0, 16),
          };
        }
        // Extra food/gold sink when a field host sits the season (supplyCostForHost curve)
        {
          const hostN = hostTotal(call.arms);
          if (hostN > 0) {
            const upkeep = applyHostUpkeep(ledger, hostN, season);
            ledger = upkeep.ledger;
          }
        }
        const clock = advanceSeason(state.clock);
        let toast: string | null = state.toast;

        // P4 Slice 5: need-driven liege horns (pressure + cooldown, not every N turns)
        {
          const seasonNow = seasonOf(clock);
          const seatPop = growth.seat ? settlementPop(growth.seat) : 0;
          const mouths =
            seatPop + garrisonTotal(call.arms) + hostTotal(call.arms);
          const hunger = hungerTag(ledger.stocks.food ?? 0, mouths);
          const decision = shouldPostLiegeCall(call, clock.turn, {
            hunger,
            season: seasonNow,
          });
          if (decision.should) {
            call = postLiegeCall(call, {
              turn: clock.turn,
              season: seasonNow,
              reasonLabel: decision.label,
            });
            toast = call.lastToast;
          } else if (ticked.log.length > 0 && call.lastToast) {
            toast = call.lastToast;
          } else if (growth.seat.pendingPromoteOrSpawn) {
          toast = canSpawnDaughter(growth.seat)
            ? `${growth.seat.name} can promote or spawn a steading (pop ${growth.seat.population}).`
            : `${growth.seat.name} can promote in place (pop ${growth.seat.population} · spawn locked).`;
          }
        }

        state = { ...state, ledger, clock, call, growth, toast };
        paint();
      },
      onSelect: (key) => {
        state = {
          ...state,
          selectedKey: key === state.selectedKey ? null : key,
        };
        paint();
      },
      onToggleClaim: (key) => {
        const result = toggleSeatClaim(state.map, state.growth, key);
        if (!result.ok) {
          state = { ...state, toast: result.reason ?? "Cannot claim", selectedKey: key };
          paint();
          return;
        }
        state = {
          ...state,
          growth: result.growth,
          selectedKey: key,
          toast:
            result.action === "unclaim"
              ? `Unclaimed ${key}`
              : `Claimed ${key}`,
        };
        paint();
      },
      onEncourageGrowth: () => {
        const result = encourageGrowth(state.growth, state.ledger.stocks.food);
        if (!result.ok) {
          state = { ...state, toast: result.reason ?? "Cannot encourage growth" };
          paint();
          return;
        }
        state = {
          ...state,
          growth: result.growth,
          ledger: {
            ...state.ledger,
            stocks: {
              ...state.ledger.stocks,
              food: Math.max(0, state.ledger.stocks.food - result.foodSpent),
            },
            lastTickLog: [
              `Encouraged growth (−${result.foodSpent} food).`,
              ...state.ledger.lastTickLog,
            ].slice(0, 16),
          },
          toast: `Growth encouraged (−${result.foodSpent} food).`,
        };
        paint();
      },
      onPromoteSeat: () => {
        if (!state.growth.seat.pendingPromoteOrSpawn) return;
        const growth = resolvePromoteSeat(state.growth);
        state = {
          ...state,
          growth,
          toast: `${growth.seat.name} promoted → ${growth.seat.tier}.`,
        };
        paint();
      },
      onSpawnVillage: () => {
        if (!state.growth.seat.pendingPromoteOrSpawn) return;
        const result = resolveSpawnVillage(state.map, state.growth, "New Steading");
        if (!result.spawned) {
          state = { ...state, toast: result.reason ?? "Spawn failed" };
          paint();
          return;
        }
        // Register banner on call/economy for chrome
        const spawned = result.spawned;
        const call = {
          ...state.call,
          bannerSeats: [
            ...state.call.bannerSeats,
            {
              key: spawned.key,
              name: spawned.name,
              garrisoned: 6,
              sent: 0,
              inTransit: 0,
            },
          ],
        };
        const ledger = {
          ...state.ledger,
          bannermen: [
            ...state.ledger.bannermen,
            {
              key: spawned.key,
              name: spawned.name,
              yieldHint: "Village tithe · growing",
              loyalty: 72,
              tradeStance: "open" as const,
              instruct: "Grow the hamlet",
            },
          ],
        };
        // Mark settlement on map cell for chrome
        const cell = state.map.cells.get(spawned.key);
        if (cell && cell.settlement === "none") {
          cell.settlement = "bannerman";
          cell.name = spawned.name;
        }
        state = {
          ...state,
          growth: result.growth,
          call,
          ledger,
          toast: `Spawned ${spawned.name} at ${spawned.key}.`,
        };
        paint();
      },
      onNewSeed: () => {
        state = buildState(freshSeed(), createClock());
        paint();
      },
      onAcceptOath: () => {
        const next = acceptOath(state.call, state.ledger);
        state = {
          ...state,
          call: next.call,
          ledger: next.ledger,
          toast: next.call.lastToast,
        };
        paint();
      },
      onCallBanners: () => {
        if (!state.call.oathAccepted) return;
        if (alreadyCalledBannersThisTurn(state.call, state.clock.turn)) {
          state = {
            ...state,
            toast: "Already called this turn — end turn first.",
          };
          paint();
          return;
        }
        const season = seasonOf(state.clock);
        const turn = state.clock.turn;
        const result = callBannermen({
          call: state.call,
          ledger: state.ledger,
          seatKey: state.map.seatKey,
          season,
          rngSeed: `${state.map.seed}|t${turn}`,
          turn,
          map: state.map,
        });
        const next = applyMuster(state.call, state.ledger, result, { turn });
        state = {
          ...state,
          call: next.call,
          ledger: next.ledger,
          toast: result.toast,
        };
        paint();
      },
      onAnswerLiege: (choice: CallOutcome, troopsSentOverride?: number) => {
        if (!state.call.oathAccepted || !state.call.liegeCallPending) return;
        const season = seasonOf(state.clock);
        const turn = state.clock.turn;
        const result = answerLiegeCall({
          call: state.call,
          ledger: state.ledger,
          choice,
          troopsSentOverride,
          season,
          seatKey: state.map.seatKey,
          rngSeed: `${state.map.seed}|answer|t${turn}`,
          turn,
          map: state.map,
        });
        const next = applyMuster(state.call, state.ledger, result, { turn });
        state = {
          ...state,
          call: next.call,
          ledger: next.ledger,
          toast: result.toast,
        };
        paint();
      },
      onMarkHornRead: (id: string) => {
        const call = ensureCallState(state.call);
        state = {
          ...state,
          call: {
            ...call,
            hornInbox: markHornRead(call.hornInbox, id),
          },
        };
        paint();
      },
      onMarkAllHornsRead: () => {
        const call = ensureCallState(state.call);
        state = {
          ...state,
          call: {
            ...call,
            hornInbox: markAllHornsRead(call.hornInbox),
          },
        };
        paint();
      },
      onConscript: (n: number) => {
        const turn = state.clock.turn;
        const r = conscriptToGarrison(state.call, state.growth, n, { turn });
        state = {
          ...state,
          call: r.call,
          growth: r.growth,
          toast: r.ok
            ? r.call.lastToast
            : r.reason ?? "Cannot conscript.",
        };
        paint();
      },
      onDischarge: (n: number) => {
        const turn = state.clock.turn;
        const r = dischargeFromGarrison(state.call, state.growth, n, { turn });
        state = {
          ...state,
          call: r.call,
          growth: r.growth,
          toast: r.ok
            ? r.call.lastToast
            : r.reason ?? "Cannot discharge.",
        };
        paint();
      },
      onReleaseHost: () => {
        const r = releaseHostToBanners(state.call, state.growth);
        state = {
          ...state,
          call: r.call,
          growth: r.growth,
          toast: r.ok
            ? r.call.lastToast
            : r.reason ?? "Cannot release host.",
        };
        paint();
      },
      onPromoteTroops: (edgeId: TrainEdgeId, qty: number, pool: PoolKind) => {
        const free = freeAbleSeat(state.growth.seat.ableBodied, state.call.arms);
        const res = promoteTroops({
          arms: state.call.arms,
          edgeId,
          qty,
          pool,
          stocks: state.ledger.stocks,
          freeAble: free,
        });
        if (!res.ok) {
          state = { ...state, toast: res.error };
          paint();
          return;
        }
        const nextAble = Math.max(0, state.growth.seat.ableBodied + res.ableDelta);
        const call = {
          ...state.call,
          arms: res.arms,
          lastToast: res.log,
        };
        state = {
          ...state,
          call,
          growth: {
            ...state.growth,
            seat: { ...state.growth.seat, ableBodied: nextAble },
          },
          ledger: {
            ...state.ledger,
            stocks: res.stocks,
            seat: {
              ...state.ledger.seat,
              garrisonHint: `Garrison ${garrisonTotal(call.arms)} · host ${hostTotal(call.arms)}`,
            },
          },
          toast: call.lastToast,
        };
        paint();
      },
      onDemoteTroops: (edgeId: TrainEdgeId, qty: number, pool: PoolKind) => {
        const res = demoteTroops({
          arms: state.call.arms,
          edgeId,
          qty,
          pool,
          stocks: state.ledger.stocks,
        });
        if (!res.ok) {
          state = { ...state, toast: res.error };
          paint();
          return;
        }
        const nextAble = Math.max(0, state.growth.seat.ableBodied + res.ableDelta);
        const call = {
          ...state.call,
          arms: res.arms,
          lastToast: res.log,
        };
        state = {
          ...state,
          call,
          growth: {
            ...state.growth,
            seat: { ...state.growth.seat, ableBodied: nextAble },
          },
          ledger: {
            ...state.ledger,
            stocks: res.stocks,
            seat: {
              ...state.ledger.seat,
              garrisonHint: `Garrison ${garrisonTotal(call.arms)} · host ${hostTotal(call.arms)}`,
            },
          },
          toast: call.lastToast,
        };
        paint();
      },
      onDismissToast: () => {
        // Clear both app toast and call.lastToast — render falls back to
        // call.lastToast when state.toast is null (oath / muster banners).
        state = {
          ...state,
          toast: null,
          call: { ...state.call, lastToast: null },
        };
        paint();
      },
    });
  };

  paint();
}

mount();
